import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { API } from "@/lib/api";
import { Download, FileImage, Calendar, Receipt, CheckCircle2, Clock } from "lucide-react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-border p-6 bg-card">
      <div className="flex items-center justify-between">
        <div className="overline text-muted-foreground">{label}</div>
        <Icon size={18} strokeWidth={1.25} className="text-primary" />
      </div>
      <div className="font-serif text-4xl mt-3">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-50 text-yellow-900 border-yellow-200",
    confirmed: "bg-accent/40 text-primary border-primary/30",
    in_progress: "bg-blue-50 text-blue-900 border-blue-200",
    completed: "bg-primary text-primary-foreground border-primary",
    cancelled: "bg-red-50 text-red-900 border-red-200",
    planning: "bg-yellow-50 text-yellow-900 border-yellow-200",
    shooting: "bg-blue-50 text-blue-900 border-blue-200",
    editing: "bg-orange-50 text-orange-900 border-orange-200",
    review: "bg-purple-50 text-purple-900 border-purple-200",
    delivered: "bg-primary text-primary-foreground border-primary",
    paid: "bg-primary text-primary-foreground border-primary",
    overdue: "bg-red-50 text-red-900 border-red-200",
    refunded: "bg-muted text-muted-foreground border-border",
  };
  return <span className={`px-2.5 py-1 text-[10px] tracking-widest uppercase border ${map[status] || "bg-muted border-border"}`}>{status.replace("_", " ")}</span>;
}

function Overview() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    // payments admin-only for now — client view can be empty
  }, []);

  return (
    <div>
      <div className="mb-10">
        <div className="overline text-primary/70">Client Portal</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-2">Hello, {user?.name?.split(" ")[0]}.</h1>
        <p className="text-muted-foreground mt-3">Everything about your commissions in one place.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        <StatCard label="Active Projects" value={projects.filter(p => p.status !== "delivered").length} icon={FileImage} />
        <StatCard label="Upcoming Bookings" value={bookings.filter(b => ["pending","confirmed"].includes(b.status)).length} icon={Calendar} />
        <StatCard label="Delivered" value={projects.filter(p => p.status === "delivered").length} icon={CheckCircle2} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="border border-border bg-card">
          <div className="p-6 border-b border-border flex justify-between items-center">
            <h3 className="font-serif text-2xl">Projects</h3>
          </div>
          <div>
            {projects.length === 0 && <div className="p-6 text-sm text-muted-foreground">No projects yet.</div>}
            {projects.map(p => (
              <div key={p.id} className="p-6 border-b border-border last:border-0" data-testid={`client-project-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-serif text-xl">{p.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{p.description}</div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                <div className="mt-4 h-1 bg-muted">
                  <div className="h-1 bg-primary" style={{ width: `${p.progress || 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border bg-card">
          <div className="p-6 border-b border-border">
            <h3 className="font-serif text-2xl">Bookings</h3>
          </div>
          <div>
            {bookings.length === 0 && <div className="p-6 text-sm text-muted-foreground">No bookings yet.</div>}
            {bookings.map(b => (
              <div key={b.id} className="p-6 border-b border-border last:border-0 flex justify-between items-center" data-testid={`client-booking-${b.id}`}>
                <div>
                  <div className="font-medium">{b.event_type}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    <Clock size={12} strokeWidth={1.25} className="inline mr-1" />
                    {new Date(b.event_date).toLocaleDateString()} · {b.event_location}
                  </div>
                </div>
                <StatusBadge status={b.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [media, setMedia] = useState([]);

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)); }, []);

  const openProject = async (p) => {
    setSelected(p);
    try {
      const { data } = await api.get(`/media/client/${p.id}`);
      setMedia(data);
    } catch {
      setMedia([]);
    }
  };

  const download = async (m) => {
    const token = localStorage.getItem("auth_token");
    const url = `${API}/media/file/${m.id}?auth=${token}`;
    window.open(url, "_blank");
  };

  return (
    <div>
      <div className="mb-10">
        <div className="overline text-primary/70">Projects</div>
        <h1 className="font-serif text-4xl mt-2">Your commissions</h1>
      </div>

      {!selected ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.length === 0 && <div className="text-sm text-muted-foreground">No projects yet.</div>}
          {projects.map((p) => (
            <button key={p.id} onClick={() => openProject(p)} className="text-left border border-border bg-card p-6 hover:border-primary" data-testid={`open-project-${p.id}`}>
              <div className="flex justify-between">
                <div className="font-serif text-2xl">{p.title}</div>
                <StatusBadge status={p.status} />
              </div>
              <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
              <div className="mt-4 h-1 bg-muted">
                <div className="h-1 bg-primary" style={{ width: `${p.progress || 0}%` }} />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <button onClick={() => { setSelected(null); setMedia([]); }} className="text-sm text-primary underline mb-6">← Back</button>
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="font-serif text-3xl">{selected.title}</h2>
              <p className="text-muted-foreground mt-1">{selected.description}</p>
            </div>
            <StatusBadge status={selected.status} />
          </div>
          <div className="overline text-primary/70 mb-4">Delivered Media ({media.length})</div>
          {media.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              No deliverables yet. You'll see them here once your project is delivered.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {media.map((m) => (
                <div key={m.id} className="border border-border bg-card group relative">
                  <div className="aspect-square bg-secondary overflow-hidden">
                    <img src={`${API}/media/file/${m.id}?auth=${localStorage.getItem("auth_token")}`} alt={m.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3 flex justify-between items-center">
                    <div className="text-xs truncate">{m.title}</div>
                    <button onClick={() => download(m)} className="text-primary" aria-label="Download" data-testid={`dl-${m.id}`}>
                      <Download size={14} strokeWidth={1.25} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BookingsPage() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => { api.get("/bookings").then(({ data }) => setBookings(data)); }, []);
  return (
    <div>
      <div className="overline text-primary/70">Bookings</div>
      <h1 className="font-serif text-4xl mt-2 mb-8">Your booking history</h1>
      <div className="border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="p-4 overline text-muted-foreground">Event</th>
              <th className="p-4 overline text-muted-foreground">Date</th>
              <th className="p-4 overline text-muted-foreground">Location</th>
              <th className="p-4 overline text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 && <tr><td colSpan={4} className="p-6 text-muted-foreground text-center">No bookings.</td></tr>}
            {bookings.map(b => (
              <tr key={b.id} className="border-t border-border">
                <td className="p-4">{b.event_type}</td>
                <td className="p-4">{new Date(b.event_date).toLocaleDateString()}</td>
                <td className="p-4">{b.event_location}</td>
                <td className="p-4"><StatusBadge status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentsPage() {
  return (
    <div>
      <div className="overline text-primary/70">Payments</div>
      <h1 className="font-serif text-4xl mt-2 mb-6">Invoices & Payments</h1>
      <div className="border border-border bg-card p-8 text-center text-muted-foreground">
        <Receipt size={28} strokeWidth={1.25} className="mx-auto text-primary mb-3" />
        Your invoices will appear here. Contact <a href="mailto:hello@dobhaiofficial.com" className="text-primary underline">billing</a> for questions.
      </div>
    </div>
  );
}

function ProfilePage() {
  const { user } = useAuth();
  return (
    <div>
      <div className="overline text-primary/70">Profile</div>
      <h1 className="font-serif text-4xl mt-2 mb-6">Account</h1>
      <div className="border border-border bg-card p-6 max-w-xl space-y-3">
        <div><span className="overline text-muted-foreground">Name</span><div className="mt-1">{user?.name}</div></div>
        <div><span className="overline text-muted-foreground">Email</span><div className="mt-1">{user?.email}</div></div>
        <div><span className="overline text-muted-foreground">Phone</span><div className="mt-1">{user?.phone || "—"}</div></div>
        <div><span className="overline text-muted-foreground">Region</span><div className="mt-1 capitalize">{user?.region}</div></div>
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  useLocation(); // force re-render on route change
  return (
    <DashboardLayout role="client">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Routes>
    </DashboardLayout>
  );
}
